import React, { useEffect, useState } from "react";
import axios from "axios";
import { API, cn, DEPARTMENT_LABELS } from "../lib/utils";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import {
  Plus,
  Trash2,
  Pencil,
  Loader2,
  ListChecks,
  ToggleLeft,
  ToggleRight,
  AlertTriangle,
} from "lucide-react";

const DEPARTMENTS = [
  { value: "merchandizing", label: "Merchandizing" },
  { value: "prepress", label: "Prepress" },
  { value: "production", label: "Production" },
  { value: "logistics", label: "Logistics" },
  { value: "packaging", label: "Packaging" },
];

export default function TaskTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(null);
  const [filterDept, setFilterDept] = useState("all");
  const [formData, setFormData] = useState({
    task_name: "",
    department: "merchandizing",
    deadline_days: 1,
    sequence: 0,
    categories: [],
  });

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/task-templates`, { withCredentials: true }),
      axios.get(`${API}/categories`, { withCredentials: true }),
    ]).then(([tmplRes, catRes]) => {
      setTemplates(tmplRes.data);
      setCategories(catRes.data);
    }).catch((err) => {
      console.error("Failed to fetch data:", err);
      toast.error("Failed to load task templates");
    }).finally(() => setLoading(false));
  }, []);

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API}/task-templates`, { withCredentials: true });
      setTemplates(res.data);
    } catch (err) {
      console.error("Failed to fetch templates:", err);
    }
  };

  const handleSave = async () => {
    if (!formData.task_name.trim()) {
      toast.error("Task name is required");
      return;
    }
    if (!formData.categories || formData.categories.length === 0) {
      toast.error("At least one category must be selected");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/task-templates/${editing}`, formData, { withCredentials: true });
        toast.success("Template updated");
      } else {
        await axios.post(`${API}/task-templates`, formData, { withCredentials: true });
        toast.success("Template created");
      }
      setShowDialog(false);
      setEditing(null);
      setFormData({ task_name: "", department: "merchandizing", deadline_days: 1, sequence: 0, categories: [] });
      fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (templateId) => {
    try {
      await axios.delete(`${API}/task-templates/${templateId}`, { withCredentials: true });
      toast.success("Template deleted");
      fetchTemplates();
    } catch (err) {
      console.error("Failed to delete template:", err);
      toast.error("Failed to delete template");
    }
  };

  const handleToggle = async (templateId) => {
    setToggling(templateId);
    try {
      await axios.put(`${API}/task-templates/${templateId}/toggle`, {}, { withCredentials: true });
      fetchTemplates();
    } catch (err) {
      console.error("Failed to toggle template:", err);
      toast.error("Failed to toggle template");
    } finally {
      setToggling(null);
    }
  };

  const openEdit = (template) => {
    setEditing(template.template_id);
    setFormData({
      task_name: template.task_name,
      department: template.department,
      deadline_days: template.deadline_days,
      sequence: template.sequence || 0,
      categories: template.categories || [],
      subcategories: template.subcategories || [],
    });
    setShowDialog(true);
  };

  const openCreate = () => {
    setEditing(null);
    setFormData({ task_name: "", department: "merchandizing", deadline_days: 1, sequence: 0, categories: [], subcategories: [] });
    setShowDialog(true);
  };

  const filtered = filterDept === "all"
    ? templates
    : templates.filter((t) => t.department === filterDept);

  const groupedByDept = filtered.reduce((acc, t) => {
    const dept = t.department;
    if (!acc[dept]) acc[dept] = [];
    acc[dept].push(t);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="space-y-6" data-testid="task-templates-loading">
        <Skeleton className="h-9 w-64" />
        <Skeleton className="h-4 w-96" />
        <Skeleton className="h-[500px]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="task-templates-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl tracking-tight">Task Templates</h1>
          <p className="text-muted-foreground mt-1">
            Manage default task templates for each department. These tasks are auto-generated when an order is approved.
          </p>
        </div>
        <Button onClick={openCreate} className="rounded-sm" data-testid="add-template-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Template
        </Button>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-3">
        <Select value={filterDept} onValueChange={setFilterDept}>
          <SelectTrigger className="w-[200px] rounded-sm h-9" data-testid="filter-department">
            <SelectValue placeholder="Filter by department" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Departments</SelectItem>
            {DEPARTMENTS.map((d) => (
              <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground">{filtered.length} templates</span>
      </div>

      {/* Templates by Department */}
      {Object.keys(groupedByDept).length === 0 ? (
        <Card className="border border-border shadow-none rounded-sm">
          <CardContent className="py-12 text-center text-muted-foreground">
            <ListChecks className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No task templates found</p>
            <p className="text-sm">Click "Add Template" to create one</p>
          </CardContent>
        </Card>
      ) : (
        Object.entries(groupedByDept)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([dept, deptTemplates]) => (
            <Card key={dept} className="border border-border shadow-none rounded-sm">
              <CardHeader className="pb-3">
                <CardTitle className="font-heading text-lg capitalize">
                  {DEPARTMENT_LABELS[dept] || dept}
                </CardTitle>
                <CardDescription>{deptTemplates.length} tasks</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[40px]">#</TableHead>
                      <TableHead>Task Name</TableHead>
                      <TableHead>Categories</TableHead>
                      <TableHead className="w-[120px]">Deadline (Days)</TableHead>
                      <TableHead className="w-[100px]">Status</TableHead>
                      <TableHead className="w-[140px] text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {deptTemplates
                      .sort((a, b) => (a.sequence || 0) - (b.sequence || 0))
                      .map((template) => (
                        <TableRow
                          key={template.template_id}
                          className={cn(!template.is_enabled && "opacity-50")}
                          data-testid={`template-row-${template.template_id}`}
                        >
                          <TableCell className="font-mono text-muted-foreground">
                            {template.sequence || "-"}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{template.task_name}</span>
                              {template.is_default && (
                                <Badge variant="outline" className="text-xs">Default</Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {(template.categories || []).length === 0 ? (
                                <span className="text-xs text-amber-600 flex items-center gap-1">
                                  <AlertTriangle className="w-3 h-3" /> No categories
                                </span>
                              ) : (template.categories || []).map((catId) => {
                                const cat = categories.find(c => c.category_id === catId);
                                return (
                                  <Badge key={catId} variant="outline" className="text-xs">
                                    {cat ? cat.name : catId}
                                  </Badge>
                                );
                              })}
                            </div>
                          </TableCell>
                          <TableCell className="font-mono">{template.deadline_days}</TableCell>
                          <TableCell>
                            <Badge
                              variant={template.is_enabled ? "default" : "secondary"}
                              className={cn(
                                "text-xs",
                                template.is_enabled ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"
                              )}
                            >
                              {template.is_enabled ? "Active" : "Disabled"}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => handleToggle(template.template_id)}
                                disabled={toggling === template.template_id}
                                data-testid={`toggle-template-${template.template_id}`}
                              >
                                {toggling === template.template_id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : template.is_enabled ? (
                                  <ToggleRight className="w-4 h-4 text-emerald-600" />
                                ) : (
                                  <ToggleLeft className="w-4 h-4 text-slate-400" />
                                )}
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => openEdit(template)}
                                data-testid={`edit-template-${template.template_id}`}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-destructive hover:text-destructive"
                                    data-testid={`delete-template-${template.template_id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete Template</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Are you sure you want to delete "{template.task_name}"? This won't affect existing tasks.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                      onClick={() => handleDelete(template.template_id)}
                                    >
                                      Delete
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ))
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Template" : "Add Task Template"}</DialogTitle>
            <DialogDescription>
              {editing ? "Update this task template." : "Create a new default task template for a department."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Task Name</Label>
              <Input
                placeholder="e.g. Order Raw Material"
                value={formData.task_name}
                onChange={(e) => setFormData((p) => ({ ...p, task_name: e.target.value }))}
                className="rounded-sm"
                data-testid="template-task-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Department</Label>
              <Select
                value={formData.department}
                onValueChange={(v) => setFormData((p) => ({ ...p, department: v }))}
              >
                <SelectTrigger className="rounded-sm" data-testid="template-department-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DEPARTMENTS.map((d) => (
                    <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Deadline (Days from order)</Label>
                <Input
                  type="number"
                  min={1}
                  value={formData.deadline_days}
                  onChange={(e) => setFormData((p) => ({ ...p, deadline_days: parseInt(e.target.value) || 1 }))}
                  className="rounded-sm"
                  data-testid="template-deadline-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Sequence Order</Label>
                <Input
                  type="number"
                  min={0}
                  value={formData.sequence}
                  onChange={(e) => setFormData((p) => ({ ...p, sequence: parseInt(e.target.value) || 0 }))}
                  className="rounded-sm"
                  data-testid="template-sequence-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Categories <span className="text-destructive">*</span></Label>
              <div className="border rounded-sm p-2 max-h-40 overflow-y-auto space-y-1">
                {categories.length === 0 ? (
                  <p className="text-xs text-muted-foreground p-2">No categories available. Create categories first.</p>
                ) : categories.map((cat) => (
                  <label key={cat.category_id} className="flex items-center gap-2 p-1.5 rounded hover:bg-muted cursor-pointer text-sm">
                    <Checkbox
                      checked={(formData.categories || []).includes(cat.category_id)}
                      onCheckedChange={(checked) => {
                        setFormData(prev => ({
                          ...prev,
                          categories: checked
                            ? [...(prev.categories || []), cat.category_id]
                            : (prev.categories || []).filter(id => id !== cat.category_id),
                          subcategories: checked
                            ? prev.subcategories
                            : (prev.subcategories || []).filter(id => {
                                const subs = (cat.subcategories || []).map(s => s.subcategory_id);
                                return !subs.includes(id);
                              }),
                        }));
                      }}
                      data-testid={`template-category-${cat.category_id}`}
                    />
                    <span>{cat.name}</span>
                    <span className="text-muted-foreground text-xs ml-auto">{cat.code}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">Only items in selected categories will generate this task</p>
            </div>
            {/* Subcategories for selected categories */}
            {(() => {
              const selectedCats = categories.filter(c => (formData.categories || []).includes(c.category_id));
              const allSubs = selectedCats.flatMap(c => (c.subcategories || []).map(s => ({ ...s, category_name: c.name })));
              if (allSubs.length === 0) return null;
              return (
                <div className="space-y-2">
                  <Label>Subcategories <span className="text-muted-foreground text-xs">(optional filter)</span></Label>
                  <div className="border rounded-sm p-2 max-h-36 overflow-y-auto space-y-1">
                    {allSubs.map((sub) => (
                      <label key={sub.subcategory_id} className="flex items-center gap-2 p-1.5 rounded hover:bg-muted cursor-pointer text-sm">
                        <Checkbox
                          checked={(formData.subcategories || []).includes(sub.subcategory_id)}
                          onCheckedChange={(checked) => {
                            setFormData(prev => ({
                              ...prev,
                              subcategories: checked
                                ? [...(prev.subcategories || []), sub.subcategory_id]
                                : (prev.subcategories || []).filter(id => id !== sub.subcategory_id)
                            }));
                          }}
                          data-testid={`template-subcategory-${sub.subcategory_id}`}
                        />
                        <span>{sub.name}</span>
                        <span className="text-muted-foreground text-xs ml-auto">{sub.category_name}</span>
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">If selected, task applies only to items with matching subcategory</p>
                </div>
              );
            })()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-template-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editing ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
